import {api} from '@/api'
import {EScene} from '@/types/scene.types'
import {ESprintenTeam} from '@repo/shared/Customer/Coop/SprintenTeams/SprintenTeams.types'
import {toastMessage} from '@repo/shared/Toast'
import {useAtom, useSetAtom} from 'jotai'
import {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react'
import FrameStore from '../Store/FrameStore'
import {
  EGameMode,
  endPositonsState,
  gameModeState,
  sceneState,
  singlePlayerDataState,
  storeWinnersDataState,
  timestampGameEndedState,
  timestampGameStartedState,
} from '../Store/atoms'
import {
  generateEndPositionMessage,
  generateInfoMessage,
  generateLeaderboardMessage,
} from './SinglePlayerMode.utils'
import {WaveButton} from './components/WaveButton'

const fStore = new FrameStore()

export const SinglePlayerMode = () => {
  const [singlePlayerData, setSinglePlayerData] = useAtom(singlePlayerDataState)
  const setGameMode = useSetAtom(gameModeState)
  const setGameStartedTimestamp = useSetAtom(timestampGameStartedState)
  const setGameEndedTimestamp = useSetAtom(timestampGameEndedState)
  const setEndPositions = useSetAtom(endPositonsState)
  const setStoreWinnersData = useSetAtom(storeWinnersDataState)
  const [scene, setScene] = useAtom(sceneState)

  const randomSelectedTeam = useRef<ESprintenTeam>()

  /**
   * Init with fake info and leaderboard message
   * This will launch the single player lobby scene
   */
  useLayoutEffect(() => {
    const infoMessage = generateInfoMessage()

    randomSelectedTeam.current =
      infoMessage.teams[Math.floor(Math.random() * infoMessage.teams.length)]

    const leaderboardMessage = generateLeaderboardMessage({
      team: randomSelectedTeam.current as ESprintenTeam,
      clicks: 0,
      percentage: 0,
    })

    fStore.setInfo(infoMessage)
    fStore.setLeaderboard(leaderboardMessage)

    setGameStartedTimestamp(null)
    setScene(EScene.LOBBY)
  }, [])

  /**
   * Get the average data for today
   */
  const isFirst = useRef(true)

  useEffect(() => {
    if (!isFirst.current) {
      return
    }
    isFirst.current = false

    api.singleMode
      .getAverageDataForToday()
      .then(data => {
        setSinglePlayerData({
          clicks: data.clicks,
          time: data.time,
          player: data.player,
          team: randomSelectedTeam.current as ESprintenTeam,
        })
        setStoreWinnersData(data.config.store_winners_data)
      })
      .catch(() => {
        // TODO: Lag en wrapper for singpleplayer, der data hentes så vises children om data er hentet
        toastMessage.error('Noe gikk galt under henting av dagens tid')
        setGameMode(EGameMode.NONE) // to avoid opening the single player mode again
        fStore.setGameMode(EGameMode.NONE)
        setScene(EScene.PRE_LOBBY)
      })
  }, [setSinglePlayerData])

  /**
   * Handle clicks on button
   */
  const [clicks, setClics] = useState(0)

  const onClick = useCallback(() => {
    setClics(prev => {
      const distance = singlePlayerData?.clicks ?? 0

      if (prev >= distance) return distance

      const newPosition = prev + 1
      const percentage = (100 / distance) * newPosition
      const leaderboardMessage = generateLeaderboardMessage({
        team: randomSelectedTeam.current as ESprintenTeam,
        clicks: newPosition,
        percentage: percentage,
      })

      fStore.setLeaderboard(leaderboardMessage)

      if (newPosition >= distance) {
        const endTimestamp = Date.now()
        setGameEndedTimestamp(endTimestamp)

        const endPositionMessage = generateEndPositionMessage(
          randomSelectedTeam.current as ESprintenTeam,
        )

        fStore.appendEndPositions({...endPositionMessage, teamEndTimestamp: endTimestamp})
        setEndPositions([{...endPositionMessage, teamEndTimestamp: endTimestamp}])
      }

      return newPosition
    })
  }, [singlePlayerData?.clicks])

  /**
   * Check for player character crossing the finish line and end the game with scoreboard
   */
  const timerRef = useRef<NodeJS.Timeout | null>(null)

  const intervalHandler = () => {
    const slotVec = fStore.getFirstPlaceTeamVec()

    if (!slotVec?.x || !fStore.getPathEndVec()?.x) return

    if (slotVec.x >= fStore.getPathEndVec().x) {
      clearInterval(timerRef.current!)
      setScene(EScene.SCOREBOARD)
    }
  }

  useEffect(() => {
    timerRef.current = setInterval(intervalHandler, 100)

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
      }
    }
  }, [])

  return scene !== EScene.STARTED ? null : (
    <div className="fixed bottom-3 left-1/2 -translate-x-1/2 transform">
      <WaveButton renderCount={clicks} onClick={onClick} />
    </div>
  )
}
